import { ReactNode, type ComponentPropsWithoutRef } from 'react';

import { FlexAligner, ModalContent, ModalFooter, ModalHeader } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { CustomReactJson } from '@/components/customReactJson';
import { StyledModal } from '@/styles';
import { responsive } from '@/utils';

type InformationModalProps<T = object> = ComponentPropsWithoutRef<typeof StyledModal> & {
  customReactJsonObject?: T;
  open: boolean;
  onClose: () => void;
  name: string;
  children?: ReactNode;
  /** Renders before footer, outside scrollable `ModalContent` (e.g. status alerts). */
  postFooter?: ReactNode;
};

export const InformationModal = ({
  customReactJsonObject,
  open,
  onClose,
  name,
  children,
  postFooter,
  ...rest
}: InformationModalProps) => {
  const { t } = useTranslation();
  return (
    <StyledModal
      $maxWidth={'44rem'}
      closeOnBackdropClick={true}
      closeOnEscapeClick={true}
      {...rest}
      open={open}
      onClose={onClose}
    >
      <ModalHeader title={name} onClose={onClose} />
      <ModalContent>
        <FlexAligner direction='column' gap={responsive.spacing['4xlarge']}>
          {children}
          {customReactJsonObject && <CustomReactJson src={customReactJsonObject} />}
        </FlexAligner>
      </ModalContent>

      <ModalFooter
        actionButtons={[
          {
            label: t('close'),
            onClick: onClose,
          },
        ]}
      />
      {postFooter}
    </StyledModal>
  );
};
